import { isDefined } from "@clipboard-health/util-ts";

import { type EvidenceDefinition, type JsonSchema } from "./types";

export function extractCursorFromUrl(url: string): string | undefined {
  try {
    const parsedUrl = new URL(url, "https://placeholder.com");
    return parsedUrl.searchParams.get("page[cursor]") ?? undefined;
  } catch {
    return undefined;
  }
}

export function cleanProperties(
  properties: Record<string, unknown>,
  schema: JsonSchema
): Record<string, unknown> {
  const schemaProperties = schema.properties ?? {};
  const cleaned: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(properties)) {
    const propertySchema = schemaProperties[key];
    if (!propertySchema) {
      continue;
    }

    if (
      propertySchema.type === "object" &&
      typeof value === "object" &&
      value !== null &&
      !Array.isArray(value)
    ) {
      const nestedCleaned = cleanProperties(value as Record<string, unknown>, propertySchema);
      if (Object.keys(nestedCleaned).length > 0) {
        cleaned[key] = nestedCleaned;
      }
    } else if (value !== "" && value !== undefined && value !== null) {
      cleaned[key] = value;
    }
  }

  return cleaned;
}

export function isUnknownEvidenceDefinition(definition: EvidenceDefinition | undefined): boolean {
  return definition?.attributes.displayName.includes("UNKNOWN") ?? false;
}

/**
 * E-sign template IDs sourced from the v1 requirement being approved
 * (`requirement.helloSignConfig.templateId` / `pandaDocConfig.templateId`).
 * These live on the requirement, never on the uploaded document, so the review
 * modal is the only place the v1→v2 linkage can be captured.
 */
export interface EsignTemplatePrefill {
  helloSignTemplateId?: string | null;
  pandaDocTemplateId?: string | null;
}

const ESIGN_PREFILL_FIELD_KEYS: ReadonlyArray<keyof EsignTemplatePrefill> = [
  "helloSignTemplateId",
  "pandaDocTemplateId",
];

/**
 * Computes which e-sign template IDs to pre-fill into the evidence properties
 * from the requirement being approved. Returns only the entries that *should* be
 * written — a key is included when all three hold:
 *   1. the selected definition's schema declares that property,
 *   2. the requirement supplies a non-empty value for it, and
 *   3. the reviewer hasn't already entered a value (we never clobber input).
 *
 * Pure: it mutates nothing and returns a partial map the caller applies via
 * `setValue`. A generic declination (no requirement config) yields `{}`, which
 * correctly leaves the optional fields blank.
 */
export function enhanceEvidenceProperties(
  currentProperties: Record<string, unknown>,
  schema: JsonSchema | undefined,
  prefill: EsignTemplatePrefill | undefined
): Partial<Record<keyof EsignTemplatePrefill, string>> {
  if (!schema || !prefill) {
    return {};
  }

  const schemaProperties = schema.properties ?? {};
  const result: Partial<Record<keyof EsignTemplatePrefill, string>> = {};

  for (const key of ESIGN_PREFILL_FIELD_KEYS) {
    const value = prefill[key];
    const isDeclaredBySchema = isDefined(schemaProperties[key]);
    const current = currentProperties[key];
    const isCurrentBlank = current === undefined || current === null || current === "";
    if (isDeclaredBySchema && isDefined(value) && value !== "" && isCurrentBlank) {
      result[key] = value;
    }
  }

  return result;
}
